import { memo, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Container,
  ExpandableSection,
  FormField,
  Grid,
  Header,
  Select,
  SpaceBetween,
  Spinner,
  Toggle,
} from '@amzn/awsui-components-core';
import {
  PublicationFeedback,
  PublicationFeedbackActivity,
  PublicationFeedbackActivityType,
} from '@amzn/awscat-pattern-library-service-model';

import SimpleRichTextInput from '../common/SimpleRichTextInput';
import SimpleRichTextOutput from '../common/SimpleRichTextOutput';
import UpdateRequestFeedbackForm from '../publication/UpdateRequestFeedbackForm';
import CharacterCountMessage from '../utils/CharacterCountMessage';
import InputSizeMessage from '../utils/InputSizeMessage';
import {
  getUpdateRequestTitle,
  publicationFeedbackStateDisplayName,
  publicationFeedbackTypeDisplayName,
  PublicationFeedbackTypeSeverity,
} from '../utils/DisplayNames';
import { AuthorActionsView } from './AuthorActionsView';
import {
  FeedbackUpdate,
  PublishedVersion,
  useUpdateRequestsTabViewModel,
} from './UpdateRequestsTabViewModel';
import {
  COMMENT_MAX_LENGTH,
  COMMENT_MIN_LENGTH,
  UpdateRequestItemViewModel,
  useUpdateRequestItemViewModel,
} from './UpdateRequestItemViewModel';

const SEVERITY_BADGE_COLORS: Record<
  Exclude<PublicationFeedbackTypeSeverity, 'NA'>,
  'severity-high' | 'severity-medium' | 'severity-low'
> = {
  High: 'severity-high',
  Medium: 'severity-medium',
  Low: 'severity-low',
};

/** One entry in a feedback item's activity timeline. */
function ActivityItem({ activity }: { readonly activity: PublicationFeedbackActivity }) {
  const userName = activity.userProfile?.name ?? 'Unknown';
  const date = activity.createdAt?.toISODate() ?? '';

  return (
    <Box>
      <Box color="text-body-secondary" fontSize="body-s">
        {userName} · {date}
      </Box>
      {activity.activityType === PublicationFeedbackActivityType.STATUS_CHANGE &&
        activity.newState && (
          <Box>
            <Box fontWeight="bold" display="inline">
              Status:
            </Box>{' '}
            {publicationFeedbackStateDisplayName.get(activity.newState)?.name ?? activity.newState}
          </Box>
        )}
      {activity.comment && (
        <Box>
          <Box fontWeight="bold" display="inline">
            Comment:
          </Box>{' '}
          <SimpleRichTextOutput value={activity.comment} />
        </Box>
      )}
    </Box>
  );
}

interface FeedbackItemProps {
  readonly feedback: PublicationFeedback;
  readonly publishedVersion: PublishedVersion | undefined;
  readonly isHighlighted: boolean;
  readonly onUpdated: (update: FeedbackUpdate) => void;
}

/** The comment editor + primary action shared by open and closed items. */
function FeedbackEditControls({ vm }: { readonly vm: UpdateRequestItemViewModel }) {
  const buttonLabel = vm.canReopen ? 'Reopen' : vm.canChangeStatus ? 'Save changes' : 'Add comment';
  const onClick = vm.canReopen ? vm.reopen : vm.canChangeStatus ? vm.save : vm.addComment;

  return (
    <SpaceBetween size="xs">
      {vm.canChangeStatus && (
        <FormField label="Status">
          <Select
            selectedOption={
              vm.selectedStatus
                ? {
                    value: vm.selectedStatus,
                    label:
                      vm.statusOptions.find((o) => o.value === vm.selectedStatus)?.label ??
                      vm.selectedStatus,
                  }
                : null
            }
            placeholder={publicationFeedbackStateDisplayName.get(vm.state)?.name ?? vm.state}
            options={vm.statusOptions.map((o) => ({ value: o.value, label: o.label }))}
            disabled={vm.isSubmitting}
            onChange={({ detail }) =>
              vm.selectStatus(
                vm.statusOptions.find((o) => o.value === detail.selectedOption.value)?.value,
              )
            }
          />
        </FormField>
      )}
      <FormField
        label={vm.isCommentRequired ? 'Comment (required)' : 'Comment'}
        errorText={vm.commentError}
      >
        <SimpleRichTextInput
          key={vm.isSubmitting ? 'submitting' : 'editable'}
          value={vm.comment}
          onChange={vm.setComment}
          disabled={vm.isSubmitting}
        />
        <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
          {/* The size/count messages only read their input's length, so feed them a
              filler string of the view model's authoritative plain-text length. */}
          <InputSizeMessage
            input={'x'.repeat(vm.commentLength)}
            minNum={COMMENT_MIN_LENGTH}
            maxNum={COMMENT_MAX_LENGTH}
          />
          <CharacterCountMessage input={'x'.repeat(vm.commentLength)} />
        </Grid>
      </FormField>
      {vm.submitError !== undefined && <Alert type="error">{vm.submitError}</Alert>}
      <Box float="right">
        <Button
          variant="primary"
          onClick={onClick}
          loading={vm.isSubmitting}
          disabled={!vm.isSubmitEnabled}
        >
          {buttonLabel}
        </Button>
      </Box>
    </SpaceBetween>
  );
}

/** A single update-request item: type, severity, status, body, activity timeline, and edit controls. */
function FeedbackItem({ feedback, publishedVersion, isHighlighted, onUpdated }: FeedbackItemProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const vm = useUpdateRequestItemViewModel(feedback, onUpdated, publishedVersion);

  // When this item is the deep-link target, then scroll it into view on mount.
  useEffect(() => {
    if (isHighlighted) {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isHighlighted]);

  if (!vm) return null;

  const data = feedback.publicationFeedbackData;
  const typeInfo = data?.issueType
    ? publicationFeedbackTypeDisplayName.get(data.issueType)
    : undefined;
  const severity = typeInfo?.severity;
  const stateLabel = publicationFeedbackStateDisplayName.get(vm.state)?.name ?? vm.state;
  const activities = [...(feedback.activities ?? [])].sort(
    (a, b) => (a.createdAt?.toMillis() ?? 0) - (b.createdAt?.toMillis() ?? 0),
  );

  const openedBy = `Opened by ${vm.submitterName} · ${vm.openedOn}`;

  return (
    <div ref={containerRef} id={`feedback-${feedback.feedbackId}`}>
      <Container
        header={
          <SpaceBetween size="xs">
            <SpaceBetween direction="horizontal" size="xs" alignItems="center">
              {severity && severity !== 'NA' && (
                <Badge color={SEVERITY_BADGE_COLORS[severity]}>{severity}</Badge>
              )}
              {vm.versionTag && <Badge color="grey">{vm.versionTag}</Badge>}
              {vm.isClosed && <Badge color="grey">Closed</Badge>}
              {vm.dueLabel && (
                <Box fontSize="body-s">{`${vm.dueLabel} | Unpublishes if unresolved`}</Box>
              )}
            </SpaceBetween>
            <Header
              variant="h3"
              description={
                vm.versionDescription ? `${openedBy} | ${vm.versionDescription}` : openedBy
              }
            >
              {getUpdateRequestTitle(data) ?? 'Update request'}
            </Header>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="m">
          <Box>
            <Box fontWeight="bold" display="inline">
              Status:
            </Box>{' '}
            {stateLabel}
          </Box>
          {data?.improvement && <SimpleRichTextOutput value={data.improvement} />}
          {data?.intendedAction && <SimpleRichTextOutput value={data.intendedAction} />}
          {activities.length > 0 && (
            <ExpandableSection headerText={`Activity (${activities.length})`} defaultExpanded>
              <SpaceBetween size="xs">
                {activities.map((activity) => (
                  <ActivityItem key={activity.activityId} activity={activity} />
                ))}
              </SpaceBetween>
            </ExpandableSection>
          )}
          <FeedbackEditControls vm={vm} />
        </SpaceBetween>
      </Container>
    </div>
  );
}

/** Read + edit "Update Requests" tab: lists publication feedback with an open/closed filter, deep-link highlight, and per-item status/comment editing. */
export const UpdateRequestsTabView = memo(function UpdateRequestsTabView() {
  const vm = useUpdateRequestsTabViewModel();
  const [isProvidingFeedback, setIsProvidingFeedback] = useState(false);

  if (!vm) return null;

  if (vm.isLoading) {
    return (
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <Spinner size="normal" />
        <Box variant="p" color="text-status-inactive">
          Loading update requests…
        </Box>
      </SpaceBetween>
    );
  }

  if (vm.loadError) {
    return (
      <Alert type="error" action={<Button onClick={vm.reload}>Retry</Button>}>
        {vm.loadError}
      </Alert>
    );
  }

  if (isProvidingFeedback) {
    return (
      <UpdateRequestFeedbackForm
        contentId={vm.contentId}
        onCancel={() => setIsProvidingFeedback(false)}
        onSubmitSuccess={() => {
          setIsProvidingFeedback(false);
          vm.reload();
        }}
      />
    );
  }

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="See an issue? Let the author know. Submit an update request to flag outdated or incorrect content. You'll be notified when it's resolved."
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <AuthorActionsView />
              <Button variant="primary" onClick={() => setIsProvidingFeedback(true)}>
                Submit a request
              </Button>
            </SpaceBetween>
          }
        >
          Update request
        </Header>
      }
    >
      <SpaceBetween size="l">
        {vm.isEmpty ? (
          <Alert type="info">
            {vm.isShowingClosed
              ? 'There are no update requests.'
              : 'There are no open update requests.'}
          </Alert>
        ) : (
          <SpaceBetween size="m">
            {vm.feedbacks.map((feedback) => (
              <FeedbackItem
                key={feedback.feedbackId}
                feedback={feedback}
                publishedVersion={
                  feedback.workflowId !== undefined
                    ? vm.publishedVersions.get(feedback.workflowId)
                    : undefined
                }
                isHighlighted={feedback.feedbackId === vm.highlightFeedbackId}
                onUpdated={vm.applyFeedbackUpdate}
              />
            ))}
          </SpaceBetween>
        )}
        <Toggle
          checked={vm.isShowingClosed}
          onChange={({ detail }) => vm.setShowClosed(detail.checked)}
        >
          Show closed requests
        </Toggle>
      </SpaceBetween>
    </Container>
  );
});
